/**
 * Taurine PDP.
 *
 * Scoped to <tr-pdp>; every query runs against the element, never the document,
 * so multiple instances (and Theme Editor section reloads) stay independent.
 *
 * Offers are quantity-based: picking one sets the real `quantity` field on the
 * product form, so the native /cart/add POST adds the correct number of units.
 * No pricing is invented here — totals are the variant's real price times the
 * chosen quantity.
 */
class TrPdp extends HTMLElement {
  connectedCallback() {
    // Shopify's {% form %} tag renders the <form>, so query it rather than
    // relying on an attribute we cannot place on it.
    this.form = this.querySelector('form.tr-buy');
    this.quantityInput = this.querySelector('[data-tr-quantity]');
    this.offerInputs = Array.from(this.querySelectorAll('[data-tr-offer]'));

    this.unitPrice = Number(this.dataset.unitPrice || 0);
    this.unitCompare = Number(this.dataset.unitCompare || 0);

    this.#initGallery();
    this.#initOffers();
    this.#initForm();
    this.#initGuide();
  }

  /* ---------------- Guide dialog ---------------- */

  #initGuide() {
    // Delegated, so any number of guide buttons work and no listener is
    // attached per block.
    this.onGuideClick = (event) => {
      const opener = event.target.closest('[data-tr-guide-open]');
      if (opener) {
        const dialog = this.querySelector(`#${CSS.escape(opener.dataset.trGuideOpen)}`);
        // showModal gives focus trapping, Esc-to-close and the backdrop for
        // free; fall back to `open` where <dialog> is unsupported.
        if (dialog?.showModal) dialog.showModal();
        else dialog?.setAttribute('open', '');
        return;
      }

      const closer = event.target.closest('[data-tr-guide-close]');
      if (closer) {
        closer.closest('[data-tr-guide]')?.close?.();
        return;
      }

      // Clicking the backdrop: the event target is the dialog itself, since
      // its own content sits in children.
      const dialog = event.target.closest('[data-tr-guide]');
      if (dialog && event.target === dialog) dialog.close();
    };
    this.addEventListener('click', this.onGuideClick);
  }

  /* ---------------- Gallery ---------------- */

  #initGallery() {
    this.track = this.querySelector('[data-tr-track]');
    if (!this.track) return;

    this.slides = Array.from(this.track.querySelectorAll('[data-tr-slide]'));
    this.thumbs = Array.from(this.querySelectorAll('[data-tr-thumb]'));
    this.prevBtn = this.querySelector('[data-tr-prev]');
    this.nextBtn = this.querySelector('[data-tr-next]');

    if (this.slides.length <= 1) {
      this.prevBtn?.setAttribute('hidden', '');
      this.nextBtn?.setAttribute('hidden', '');
    }

    this.thumbs.forEach((t, i) => t.addEventListener('click', () => this.#goToSlide(i)));
    this.prevBtn?.addEventListener('click', () => this.#step(-1));
    this.nextBtn?.addEventListener('click', () => this.#step(1));

    // root is the scroller itself, so page scrolling never changes the active
    // slide — only horizontal movement within the track does.
    this.galleryObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) this.#setActiveSlide(this.slides.indexOf(e.target));
        });
      },
      { root: this.track, threshold: 0.6 }
    );
    this.slides.forEach((s) => this.galleryObserver.observe(s));
    this.#setActiveSlide(0);
  }

  #goToSlide(i) {
    const slide = this.slides?.[i];
    if (!slide) return;
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    // `inline: 'start'` scrolls the track only; `block: 'nearest'` keeps the
    // page from jumping vertically.
    slide.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'nearest', inline: 'start' });
  }

  #step(delta) {
    const next = Math.min(Math.max((this.activeSlide ?? 0) + delta, 0), this.slides.length - 1);
    this.#goToSlide(next);
  }

  #setActiveSlide(i) {
    if (i < 0) return;
    this.activeSlide = i;
    this.thumbs?.forEach((t, n) => {
      t.classList.toggle('is-active', n === i);
      t.setAttribute('aria-current', n === i ? 'true' : 'false');
    });
    if (this.prevBtn) this.prevBtn.disabled = i <= 0;
    if (this.nextBtn) this.nextBtn.disabled = i >= this.slides.length - 1;
  }

  /* ---------------- Offers ---------------- */

  #initOffers() {
    if (!this.offerInputs.length) return;
    this.offerInputs.forEach((i) => i.addEventListener('change', () => this.#onOfferChange()));
    this.#onOfferChange();
  }

  #activeOffer() {
    return this.offerInputs.find((i) => i.checked) || null;
  }

  #onOfferChange() {
    const offer = this.#activeOffer();

    this.offerInputs.forEach((i) => {
      i.closest('[data-tr-offer-card]')?.classList.toggle('is-selected', i.checked);
    });

    const qty = Math.max(1, Number(offer?.dataset.trQty || 1));
    if (this.quantityInput) this.quantityInput.value = String(qty);

    this.#renderCtaPrice(qty);
  }

  #money(cents) {
    const fmt = this.dataset.moneyFormat || '${{amount}}';
    const amount = (cents / 100).toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
    return fmt.replace(/\{\{\s*amount[^}]*\}\}/, amount);
  }

  #renderCtaPrice(qty) {
    const priceEl = this.querySelector('[data-tr-cta-price]');
    const compareEl = this.querySelector('[data-tr-cta-compare]');
    if (!priceEl) return;

    const total = this.unitPrice * qty;
    priceEl.textContent = this.#money(total);

    if (compareEl) {
      const compareTotal = this.unitCompare * qty;
      // Only ever show a strikethrough backed by a real compare-at price.
      const show = this.unitCompare > this.unitPrice && compareTotal > total;
      compareEl.hidden = !show;
      if (show) compareEl.textContent = this.#money(compareTotal);
    }
  }

  /* ---------------- Cart ---------------- */

  #initForm() {
    if (!this.form) return;
    this.form.addEventListener('submit', () => {
      const btn = this.querySelector('[data-tr-submit]');
      if (!btn) return;
      if (this.submitting) return;

      this.submitting = true;
      btn.setAttribute('aria-busy', 'true');
      // Not `disabled`: a disabled submit button is omitted from the POST, and
      // the `name="add"` pair has to reach Shopify.
      btn.setAttribute('aria-disabled', 'true');

      // Native navigation follows. Restore state if the browser returns the
      // page from bfcache instead of reloading it.
      window.addEventListener(
        'pageshow',
        () => {
          this.submitting = false;
          btn.removeAttribute('aria-busy');
          btn.removeAttribute('aria-disabled');
        },
        { once: true }
      );
    });
  }

  disconnectedCallback() {
    this.galleryObserver?.disconnect();
    if (this.onGuideClick) this.removeEventListener('click', this.onGuideClick);
  }
}

if (!customElements.get('tr-pdp')) customElements.define('tr-pdp', TrPdp);
